import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useI18n } from "../i18n";
import { cancelReservation, getReservation, type ManageView } from "../lib/api";
import { themeStyle } from "../lib/theme";
import { localizedDate } from "../../shared/dates";
import { themeForDate } from "../../shared/event";
import { ArrowLeft, Search } from "../components/Icon";

export function Manage() {
  const { token = "" } = useParams();
  const { t, lang } = useI18n();
  const [state, setState] = useState<"loading" | "ok" | "notfound">("loading");
  const [data, setData] = useState<ManageView | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getReservation(token)
      .then((d) => {
        setData(d);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [token]);

  async function onCancel() {
    if (!confirm(t("manage.cancelConfirm"))) return;
    setCancelling(true);
    try {
      await cancelReservation(token);
      setData((d) => (d ? { ...d, reservation: { ...d.reservation, status: "cancelled" } } : d));
    } catch {
      /* ignore — UI stays as-is */
    } finally {
      setCancelling(false);
    }
  }

  if (state === "loading") return <div className="container-x py-24 text-center text-ink/50">{t("common.loading")}</div>;
  if (state === "notfound" || !data)
    return (
      <div className="container-x py-24 text-center">
        <Search className="mx-auto h-12 w-12 text-ink/40" />
        <p className="mt-4 text-ink/70">{t("manage.notFound")}</p>
        <Link to="/" className="btn-primary mt-6">{t("common.backHome")}</Link>
      </div>
    );

  const r = data.reservation;
  const slot = data.slot;
  const theme = slot ? themeForDate(slot.event_date) : "brand";
  const cancelled = r.status === "cancelled";

  return (
    <div style={themeStyle(theme)} className="container-x max-w-lg py-12">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-[var(--theme-deep)]">
        <ArrowLeft className="h-4 w-4" /> {t("common.backHome")}
      </Link>
      <h1 className="section-title mt-3">{t("manage.title")}</h1>

      <div className="card mt-6">
        <div className="text-center">
          <div className="text-xs text-ink/50">{t("reserve.yourCode")}</div>
          <div className="text-3xl font-extrabold tracking-widest text-[var(--theme-deep)]">{r.code}</div>
        </div>
        <dl className="mt-5 space-y-2 text-sm">
          <Row k={t("reserve.name")} v={r.name} />
          {slot && <Row k={t("reserve.day")} v={localizedDate(slot.event_date, lang)} />}
          {slot && <Row k={t("reserve.round")} v={`${slot.start_time}–${slot.end_time} น.`} />}
          <Row k={t("reserve.partySize")} v={`${r.party_size}`} />
          <div className="flex justify-between gap-4 pt-2">
            <dt className="text-ink/50">{t("manage.status")}</dt>
            <dd>
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${
                  cancelled ? "bg-black/5 text-ink/50" : "bg-green-100 text-green-700"
                }`}
              >
                {cancelled ? t("manage.cancelled") : t("manage.confirmed")}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {cancelled ? (
        <p className="mt-6 text-center text-sm text-ink/60">{t("manage.cancelledMsg")}</p>
      ) : (
        <button onClick={onCancel} disabled={cancelling} className="btn-ghost mt-6 w-full !text-red-500">
          {cancelling ? t("manage.cancelling") : t("manage.cancelBtn")}
        </button>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink/50">{k}</dt>
      <dd className="font-semibold text-ink">{v}</dd>
    </div>
  );
}
